/* eslint-disable @nx/enforce-module-boundaries */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  FieldConfig,
  FilterOperator,
  FilterType,
  FilterValue,
  PagedResultDto,
} from '@heitown/common-interfaces';
import * as moment from 'moment';
import { map, Observable } from 'rxjs';
import { ApiClientConfig } from '../api-client-config';

@Injectable()
export abstract class BaseApiClient<T> {
  abstract baseUrl: string;

  constructor(protected http: HttpClient, protected config: ApiClientConfig) {}

  getAll() {
    return this.http.get<T[]>(this.baseUrl);
  }

  getPaged(
    take: number,
    skip: number,
    orderBy?: { [field: string]: 'asc' | 'desc' },
    filters?: FilterValue[],
    join?: string[]
  ) {
    return this.http.post<PagedResultDto<T>>(`${this.baseUrl}/search`, {
      skip,
      take,
      orderBy,
      filters,
      join,
    });
  }

  downloadExcel(
    fileName?: string,
    columns?: FieldConfig[],
    filters?: FilterValue[],
    orderBy?: { [field: string]: 'asc' | 'desc' },
    join?: string[],
    apiURl = '/download-excel'
  ): Observable<ArrayBuffer> {
    const mappedFilters = this.mapFilters(filters);

    return this.http.post(
      this.baseUrl + apiURl,
      {
        fileName,
        columns,
        filters: mappedFilters || [],
        orderBy,
        join,
      },
      {
        responseType: 'arraybuffer',
      }
    );
  }

  protected mapFilters(filters: FilterValue[] | undefined) {
    return filters?.map((filter) => {
      if (
        filter.type !== FilterType.datetime &&
        filter.type !== FilterType.date
      )
        return filter;
      else {
        switch (filter.operator) {
          case FilterOperator.equals:
            return {
              ...filter,
              operator: FilterOperator.inRange,
              value: [
                moment(filter.value[0]).startOf('d').toISOString(),
                moment(filter.value[0]).endOf('d').toISOString(),
              ],
            };
          case FilterOperator.greaterThan:
            return {
              ...filter,
              value: [moment(filter.value[0]).endOf('d').toISOString()],
            };
          case FilterOperator.greaterThanOrEqual:
            return {
              ...filter,
              value: [moment(filter.value[0]).startOf('d').toISOString()],
            };
          case FilterOperator.lessThan:
            return {
              ...filter,
              value: [moment(filter.value[0]).startOf('d').toISOString()],
            };
          case FilterOperator.lessThanOrEqual:
            return {
              ...filter,
              value: [moment(filter.value[0]).endOf('d').toISOString()],
            };

          case FilterOperator.inRange:
            return {
              ...filter,
              value: [
                moment(filter.value[0]).startOf('d').toISOString(),
                moment(filter.value[1]).endOf('d').toISOString(),
              ],
            };

          default:
            return {
              ...filter,
            };
        }
      }
    });
  }

  downloadPdf(
    fileName?: string,
    columns?: FieldConfig[],
    filters?: FilterValue[],
    orderBy?: { [field: string]: 'asc' | 'desc' },
    join?: string[]
  ): Observable<ArrayBuffer> {
    return this.http
      .post(
        this.baseUrl + '/download-pdf',
        { fileName, columns, filters, orderBy, join },
        {
          responseType: 'arraybuffer',
        }
      )
      .pipe(
        map((file: ArrayBuffer) => {
          return file;
        })
      );
  }

  downloadLabel() {
    return this.http.get(this.baseUrl + '/download-label');
  }
}
